/**
 * Forum (Talent School) translations. One language shown at a time based on user selection.
 */
export type ForumLang = "es" | "en";

export const forumTranslations = {
  es: {
    // Header
    schoolTitle: "Escuela de Talentos",
    registrationSubtitle: "Inscripción Talent School",
    // Step labels (stepper)
    stepStudent: "Estudiante",
    stepFamily: "Familia",
    stepSafety: "Seguridad",
    stepPayment: "Pago",
    // Matrícula modal (view pending payment)
    viewPendingPayment: "Gestionar usuario",
    viewPendingPaymentDesc: "Ingresa tu matrícula (código de estudiante).",
    gestionDeClases: "Gestión de clases",
    gestionDeClasesSub: "Ingrese su matrícula para acceder",
    registrationIdLabel: "Matrícula",
    registrationIdPlaceholder: "Ej. 1230123",
    viewPayment: "Acceder",
    close: "Cerrar",
    registrationIdRequired: "Escribe tu matrícula.",
    // General
    backToHome: "Volver al inicio",
    // Child info
    studentInfo: "Información del Estudiante",
    fullName: "Nombre completo *",
    fullNamePlaceholder: "Nombre del estudiante",
    studentSearchNoResults: "No se encontraron coincidencias",
    school: "Escuela *",
    schoolPlaceholder: "Escuela actual",
    selectSchool: "Selecciona escuela",
    hasInstitutionalCode: "¿Ya tiene código institucional (American School)?",
    hasInstitutionalCodeYes: "Sí, tengo código",
    hasInstitutionalCodeNo: "No, no tengo (lo crearemos al completar)",
    institutionalCodePlaceholder: "Código institucional",
    age: "Edad *",
    selectAge: "Selecciona edad",
    ageYears: (n: number) => `${n} años`,
    // Parent info
    familyDetails: "Datos de los Padres",
    housePhone: "Teléfono residencia",
    housePhonePlaceholder: "809 - 000 - 0000",
    motherContact: "Madre",
    fatherContact: "Padre",
    fullNamePlaceholderShort: "Nombre completo",
    cellPhone: "Teléfono celular",
    addMother: "+ Añadir contacto de madre",
    addFather: "+ Añadir contacto de padre",
    parentEmail: "Correo del padre/madre *",
    // Safety
    emergencyMedical: "Seguridad y Salud",
    addContact: "+ Añadir contacto",
    emergencyContactN: (n: number) => `Contacto de emergencia #${n}`,
    relationship: "Parentesco",
    selectRelation: "Selecciona parentesco",
    phone: "Teléfono",
    namePlaceholder: "Nombre",
    medicalConditionsLabel: "¿Alguna alergia, condición física o emocional diagnosticada?",
    medicalConditionsPlaceholder: "Condiciones médicas...",
    // Class selection
    classSelection: "Clases y Talleres",
    total: "Total",
    loadingClasses: "Cargando clases…",
    noClassesAvailable: "No hay clases disponibles. El administrador puede cargar clases desde la pestaña Clases.",
    cupos: "Cupos",
    link: "Enlace",
    email: "Email",
    // Review & pay
    choosePaymentMethod: "Elige forma de pago",
    paymentMethodDesc: "Puedes pagar tus clases de forma segura en línea ahora o visitar la oficina de la escuela para pago manual.",
    payOnline: "Pagar en línea",
    secureCard: "Tarjeta de crédito/débito segura",
    payAtSchool: "Pagar en la escuela",
    manualPayment: "Pago manual en oficina",
    orderTotal: "Total del pedido",
    paySecurelyDesc:
      "Paga de forma segura con tu tarjeta. Serás redirigido a la pasarela de pago segura de Azul para completar la transacción.",
    paySecurely: "Pagar de forma segura",
    submitting: "Enviando...",
    completeEnrollment: "Completar inscripción",
    completeAllFields: "Completa todos los campos obligatorios del formulario antes de pagar. Vuelve atrás y rellena la información faltante.",
    // Success
    enrollmentSubmitted: "¡Inscripción recibida!",
    thankYou: "¡Gracias! Hemos recibido su inscripción.",
    saveRegistrationId: "Guarda tu matrícula para pagar después",
    registrationIdHint: "No necesitas correo ni contraseña. En «¿Ya enviaste el formulario? Entra con tu matrícula» ingresa este código para ver y completar el pago.",
    saveMatricula: "Tu matrícula",
    matriculaHint: "En «¿Ya enviaste el formulario? Entra con tu matrícula» ingresa este código para ver y completar el pago. No necesitas correo ni contraseña.",
    returnHome: "Volver al inicio",
    // Buttons
    back: "Anterior",
    next: "Continuar",
    // Already submitted CTA
    alreadySubmittedCta: "¿Ya inscrito? Accede con tu código",
    // Validation
    validationChildName: "Por favor ingresa el nombre completo del estudiante.",
    validationSchool: "Por favor ingresa la escuela.",
    validationAge: "Por favor selecciona la edad del estudiante.",
    validationParentEmail: "Por favor ingresa el correo del padre/madre.",
    validationMother: "Por favor ingresa al menos el nombre o celular de la madre.",
    validationFather: "Por favor ingresa al menos el nombre o celular del padre.",
    validationEmergency: "Por favor añade al menos un contacto de emergencia con nombre y teléfono válido de 10 dígitos.",
    validationClass: "Por favor selecciona al menos una clase para continuar.",
    validationInstitutionalCode: "Por favor ingresar código de matrícula.",
    currentClassesNote: "Clases actuales (puedes añadir o cambiar):",
    inscriptionsOpenFor: "Inscripciones abiertas para",
    // Loading
    loadingForm: "Cargando tu formulario…",
    changingLanguage: "Cambiando idioma...",
    // Footer
    adminAccess: "Acceso administrador",
    footerPrivacy: "Privacidad",
    footerRefunds: "Reembolsos",
    footerTerms: "Términos",
    // Relation for auto-fill (stored value)
    relationMother: "Madre / Mother",
    relationFather: "Padre / Father",
    // Fiscal receipt prompt
    fiscalReceiptPrompt: "¿Desea comprobante fiscal?",
    fiscalReceiptYes: "Sí",
    fiscalReceiptNo: "No",
    noComprobanteLabel: "No deseo comprobante fiscal",
    changeComprobante: "Cambiar",
    yesComprobanteLabel: "Sí, deseo comprobante fiscal",
    editComprobante: "Editar",
    indicatePaymentFrequency: "Indique frecuencia de pago.",
    fiscalDataSection: "Datos fiscales",
    fiscalNameLabel: "Nombre fiscal",
    fiscalNamePlaceholder: "Razón social o nombre",
    documentTypeLabel: "Tipo de documento",
    documentNumberLabel: "Número de documento",
    documentNumberPlaceholder: "RNC o cédula",
    comprobanteTypeLabel: "Tipo de comprobante (DGII)",
    paymentTermsNote: "Al realizar esta compra usted acepta los",
    paymentTermsNoteAnd: "y las",
    paymentTermsLink: "Términos de uso",
    paymentRefundsLink: "Políticas de devolución",
    paymentProcessedBy: "Pagos procesados de forma segura por Azul",
    paymentFrequencySection: "Frecuencia de pago",
    freqBimestral: "Bimestral",
    freqCuatrimestral: "Cuatrimestral",
    freqMonths: (n: number) => `${n} meses`,
    freqSelected: "Seleccionado",
    freqConfirm: "Confirmar",
    cuposOcupados: "cupos ocupados",
    manualPaymentNoCupoAlert: "No tendrá cupo reservado hasta que realices el pago en la oficina.",
    // Errors
    registrationFailed: "Error al inscribir. Intenta de nuevo.",
    restoreFailed: "No se pudo cargar la inscripción.",
    failedToLoadClasses: "Error al cargar las clases",
    duplicateStudentCodeTitle: "Código de matrícula ya registrado",
    duplicateStudentCodeMessage: "Ya existe un estudiante con este código de matrícula, favor de revisar. Si ya estás inscrito, usa «¿Ya enviaste el formulario? Entra con tu matrícula» para pagar o seleccionar nuevas clases.",
  },
  en: {
    schoolTitle: "Talent School",
    registrationSubtitle: "Talent School Registration",
    stepStudent: "Student",
    stepFamily: "Family",
    stepSafety: "Safety",
    stepPayment: "Payment",
    viewPendingPayment: "Manage user",
    viewPendingPaymentDesc: "Enter your Student School ID (matrícula).",
    gestionDeClases: "Class management",
    gestionDeClasesSub: "Enter your enrollment code to access",
    registrationIdLabel: "Student School ID",
    registrationIdPlaceholder: "e.g. 1230123",
    viewPayment: "Access",
    close: "Close",
    registrationIdRequired: "Please enter your Student School ID.",
    backToHome: "Back to home",
    studentInfo: "Student Information",
    fullName: "Full name *",
    fullNamePlaceholder: "Student's name",
    studentSearchNoResults: "No matches found",
    school: "School *",
    schoolPlaceholder: "Current school",
    selectSchool: "Select school",
    hasInstitutionalCode: "Do you already have an institutional code (American School)?",
    hasInstitutionalCodeYes: "Yes, I have a code",
    hasInstitutionalCodeNo: "No (we'll create one when you complete)",
    institutionalCodePlaceholder: "Institutional code",
    age: "Age *",
    selectAge: "Select age",
    ageYears: (n: number) => `${n} years`,
    familyDetails: "Family Details",
    housePhone: "House phone",
    housePhonePlaceholder: "809 - 000 - 0000",
    motherContact: "Mother",
    fatherContact: "Father",
    fullNamePlaceholderShort: "Full name",
    cellPhone: "Cell phone",
    addMother: "+ Add mother's contact",
    addFather: "+ Add father's contact",
    parentEmail: "Parent email *",
    emergencyMedical: "Emergency & Medical",
    addContact: "+ Add contact",
    emergencyContactN: (n: number) => `Emergency contact #${n}`,
    relationship: "Relationship",
    selectRelation: "Select relation",
    phone: "Phone",
    namePlaceholder: "Name",
    medicalConditionsLabel: "Any diagnosed allergy, physical or emotional conditions?",
    medicalConditionsPlaceholder: "Medical conditions...",
    classSelection: "Classes & Workshops",
    total: "Total",
    loadingClasses: "Loading classes…",
    noClassesAvailable: "No classes available. Admin can seed forum classes from the Classes tab.",
    cupos: "Slots",
    link: "Link",
    email: "Email",
    choosePaymentMethod: "Choose payment method",
    paymentMethodDesc: "You can securely pay for your classes online now or visit our school office for manual payment.",
    payOnline: "Pay online",
    secureCard: "Secure credit/debit card",
    payAtSchool: "Pay at school",
    manualPayment: "Manual payment at office",
    orderTotal: "Order total",
    paySecurelyDesc:
      "Pay securely with your card. You will be redirected to Azul's secure payment gateway to complete the transaction.",
    paySecurely: "Pay securely",
    submitting: "Submitting...",
    completeEnrollment: "Complete enrollment",
    completeAllFields: "Please complete all required fields in the form before paying. Go back and fill in any missing information.",
    enrollmentSubmitted: "Enrollment received!",
    thankYou: "Thank you! We have received your registration.",
    saveRegistrationId: "Save your Student School ID to pay later",
    registrationIdHint: "You don't need email or password. In «Already submitted? Enter your Student School ID» enter this code to view and complete payment.",
    saveMatricula: "Your Student School ID",
    matriculaHint: "In «Already submitted? Enter your Student School ID» enter this code to view and complete payment. You don't need email or password.",
    returnHome: "Return home",
    back: "Back",
    next: "Next",
    alreadySubmittedCta: "Already enrolled? Access with your code",
    validationChildName: "Please enter the child's full name.",
    validationSchool: "Please enter the school.",
    validationAge: "Please select the child's age.",
    validationParentEmail: "Please enter the parent email.",
    validationMother: "Please enter at least mother's name or cell phone.",
    validationFather: "Please enter at least father's name or cell phone.",
    validationEmergency: "Please add at least one emergency contact with name and a valid 10-digit phone.",
    validationClass: "Please select at least one class to continue.",
    validationInstitutionalCode: "Please enter school student id code.",
    currentClassesNote: "Current classes (you can add or change):",
    inscriptionsOpenFor: "Registrations open for",
    loadingForm: "Loading your form…",
    changingLanguage: "Changing language...",
    adminAccess: "Admin access",
    footerPrivacy: "Privacy",
    footerRefunds: "Refunds",
    footerTerms: "Terms",
    relationMother: "Madre / Mother",
    relationFather: "Padre / Father",
    fiscalReceiptPrompt: "Do you want a fiscal receipt?",
    fiscalReceiptYes: "Yes, I want a receipt",
    fiscalReceiptNo: "No",
    noComprobanteLabel: "I don't want a fiscal receipt",
    changeComprobante: "Change",
    yesComprobanteLabel: "Yes, I want a fiscal receipt",
    editComprobante: "Edit",
    indicatePaymentFrequency: "Indicate payment frequency.",
    fiscalDataSection: "Fiscal data",
    fiscalNameLabel: "Fiscal name",
    fiscalNamePlaceholder: "Company or full name",
    documentTypeLabel: "Document type",
    documentNumberLabel: "Document number",
    documentNumberPlaceholder: "RNC or ID number",
    comprobanteTypeLabel: "Receipt type (DGII)",
    paymentTermsNote: "By making this purchase you accept the",
    paymentTermsNoteAnd: "and the",
    paymentTermsLink: "Terms of use",
    paymentRefundsLink: "Refund policies",
    paymentProcessedBy: "Payments securely processed by Azul",
    paymentFrequencySection: "Payment frequency",
    freqBimestral: "Bimonthly",
    freqCuatrimestral: "Quarterly",
    freqMonths: (n: number) => `${n} months`,
    freqSelected: "Selected",
    freqConfirm: "Confirm",
    cuposOcupados: "spots taken",
    manualPaymentNoCupoAlert: "No spot will be reserved until you complete payment at the office.",
    registrationFailed: "Registration failed. Please try again.",
    restoreFailed: "Could not load registration.",
    failedToLoadClasses: "Failed to load classes",
    duplicateStudentCodeTitle: "Student School ID already registered",
    duplicateStudentCodeMessage: "A student with this School ID already exists. Please double-check. If you are already enrolled, use “Already submitted? Enter your ID” to pay or select new classes.",
  },
} as const;

type AllKeys = keyof typeof forumTranslations.es;
type StringKey = { [K in AllKeys]: typeof forumTranslations.es[K] extends string ? K : never }[AllKeys];

export function t(lang: ForumLang, key: StringKey): string {
  return forumTranslations[lang][key] as string;
}

/** For translation keys that are functions (e.g. ageYears(n), emergencyContactN(n)) */
export function tfn(
  lang: ForumLang,
  key: "ageYears" | "emergencyContactN" | "freqMonths",
  arg: number
): string {
  const value = forumTranslations[lang][key];
  return (value as (n: number) => string)(arg);
}
